import api from '@/api'
import graficosConsumoMethods from './graficosConsumo'
import {
  calcularPaginasVisiveis,
  formatOperacao,
  formatarCusto,
  formatarLatencia,
  uniqueValues,
} from './helpers'

export default {
  name: 'RelatoriosIAConsumo',
  props: {
    filtrosPeriodo: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      filtros: {
        modelo: '',
        plataforma: '',
        operacao: '',
        oportunidadeId: '',
      },
      opcoesFiltro: {
        modelos: [],
        plataformas: [],
        operacoes: [],
      },
      metricas: {},
      serieTokens: { series: [], categories: [] },
      serieCusto: { series: [], categories: [] },
      serieEntradaSaida: { series: [], categories: [] },
      interacoes: {
        data: [],
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: 0,
        from: 0,
        to: 0,
        prev_page_url: null,
        next_page_url: null,
      },
      metricaModal: null,
    }
  },
  computed: {
    cardsMetricas() {
      const g = this.metricas?.metricas_gerais
      if (!g) {
        return []
      }

      return [
        {
          id: 'interacoes',
          titulo: 'Interações IA',
          valor: g.total_interacoes.toLocaleString('pt-BR'),
          cor: 'primary',
          icone: 'fa-robot',
        },
        {
          id: 'tokens',
          titulo: 'Tokens Totais',
          valor: g.tokens_totais.toLocaleString('pt-BR'),
          cor: 'info',
          icone: 'fa-coins',
        },
        {
          id: 'custo',
          titulo: 'Custo Estimado',
          valor: '$' + formatarCusto(g.custo_estimado),
          cor: 'success',
          icone: 'fa-dollar-sign',
        },
        {
          id: 'latencia',
          titulo: 'Latência Média',
          valor: formatarLatencia(g.latencia_media_ms),
          cor: 'warning',
          icone: 'fa-clock',
        },
        {
          id: 'taxa_sucesso',
          titulo: 'Taxa de Sucesso',
          valor: g.taxa_sucesso + '%',
          cor: 'purple',
          icone: 'fa-check-circle',
        },
        {
          id: 'modelo_top',
          titulo: 'Modelo Mais Utilizado',
          valor: g.modelo_mais_usado || 'N/A',
          cor: 'secondary',
          icone: 'fa-microchip',
        },
      ]
    },
    definicoesMetricas() {
      const g = this.metricas?.metricas_gerais || {}

      return {
        interacoes: {
          id: 'interacoes',
          titulo: 'Interações IA',
          valor: (g.total_interacoes ?? 0).toLocaleString('pt-BR'),
          cor: 'primary',
          icone: 'fa-robot',
          descricao: 'Quantidade total de chamadas registradas à API de inteligência artificial no período selecionado. Cada completion (incluindo continuações por limite de tokens) conta como uma interação.',
          comoCalculado: 'Soma de todas as interações registradas para a loja no período, respeitando os filtros aplicados.',
          detalhes: [
            'Inclui chat_completion e chat_completion_continuation.',
            'Uma mesma conversa pode gerar mais de uma interação.',
          ],
        },
        tokens: {
          id: 'tokens',
          titulo: 'Tokens Totais',
          valor: (g.tokens_totais ?? 0).toLocaleString('pt-BR'),
          cor: 'info',
          icone: 'fa-coins',
          descricao: 'Volume total de tokens consumidos pela IA. Tokens de entrada correspondem ao contexto enviado (prompt, histórico, tools). Tokens de saída correspondem ao texto gerado pelo modelo.',
          comoCalculado: 'Soma de tokens_totais de cada log. Entrada e saída são somadas separadamente a partir dos campos tokens_entrada e tokens_saida.',
          detalhes: [
            `Entrada: ${(g.tokens_entrada ?? 0).toLocaleString('pt-BR')} tokens`,
            `Saída: ${(g.tokens_saida ?? 0).toLocaleString('pt-BR')} tokens`,
            'Entrada costuma ser maior quando há histórico longo ou muitas ferramentas disponíveis.',
          ],
        },
        custo: {
          id: 'custo',
          titulo: 'Custo Estimado',
          valor: '$' + formatarCusto(g.custo_estimado ?? 0),
          cor: 'success',
          icone: 'fa-dollar-sign',
          descricao: 'Estimativa do custo das interações com IA em dólares (USD), com base na tabela de preços configurada por modelo.',
          comoCalculado: 'Soma do campo custo_estimado de cada log, calculado a partir dos tokens de entrada e saída e do preço do modelo utilizado.',
          detalhes: [
            'Valor aproximado — consulte a fatura real do provedor para conferência.',
            'Pode variar se o provedor atualizar preços ou se houver modelos sem tabela cadastrada.',
          ],
        },
        latencia: {
          id: 'latencia',
          titulo: 'Latência Média',
          valor: formatarLatencia(g.latencia_media_ms),
          cor: 'warning',
          icone: 'fa-clock',
          descricao: 'Tempo médio de resposta da API de IA, medido do envio da requisição até o retorno da resposta.',
          comoCalculado: 'Média aritmética do campo latencia_ms de todas as interações do período.',
          detalhes: [
            'Valores menores indicam respostas mais rápidas.',
            'Picos de latência podem ocorrer em horários de maior uso ou com modelos mais pesados.',
          ],
        },
        taxa_sucesso: {
          id: 'taxa_sucesso',
          titulo: 'Taxa de Sucesso',
          valor: (g.taxa_sucesso ?? 0) + '%',
          cor: 'purple',
          icone: 'fa-check-circle',
          descricao: 'Percentual de interações concluídas com sucesso (status_mensagem = success) em relação ao total de interações.',
          comoCalculado: '(Interações com sucesso ÷ Total de interações) × 100.',
          detalhes: [
            'Falhas podem indicar timeout, erro da API ou resposta inválida.',
            'Quanto mais próximo de 100%, mais estável está o consumo de IA no período.',
          ],
        },
        modelo_top: {
          id: 'modelo_top',
          titulo: 'Modelo Mais Utilizado',
          valor: g.modelo_mais_usado || 'N/A',
          cor: 'secondary',
          icone: 'fa-microchip',
          descricao: 'Modelo de IA mais utilizado no período, considerando a quantidade de interações registradas.',
          comoCalculado: 'Modelo com maior quantidade de interações no período.',
          detalhes: [
            'Útil para entender qual modelo concentra a maior parte do consumo.',
            'Consulte o gráfico "Tokens por Modelo" para ver a distribuição completa.',
          ],
        },
      }
    },
    paginasVisiveis() {
      return calcularPaginasVisiveis(
        this.interacoes.current_page,
        this.interacoes.last_page
      )
    },
  },
  watch: {
    filtrosPeriodo: {
      deep: true,
      handler() {
        this.carregarDados()
      },
    },
  },
  mounted() {
    if (!window.graficosConsumoIA) {
      window.graficosConsumoIA = []
    }
    this.carregarDados()
  },
  beforeDestroy() {
    this.destruirGraficosConsumo()
  },
  methods: {
    ...graficosConsumoMethods,
    getParamsBase() {
      return {
        lojaId: this.filtrosPeriodo.lojaId || window.lojaId,
        startDate: this.filtrosPeriodo.startDate,
        endDate: this.filtrosPeriodo.endDate,
        modelo: this.filtros.modelo || undefined,
        plataforma: this.filtros.plataforma || undefined,
        operacao: this.filtros.operacao || undefined,
        oportunidadeId: this.filtros.oportunidadeId || undefined,
      }
    },
    async carregarDados() {
      if (!this.filtrosPeriodo.startDate || !this.filtrosPeriodo.endDate) {
        return
      }

      document.getElementById('overlayer-loading')?.classList.add('show-overlayer')

      try {
        await Promise.all([
          this.carregarFiltros(),
          this.carregarMetricas(),
          this.carregarSeries(),
          this.carregarInteracoes(1),
        ])

        this.$nextTick(() => {
          this.renderTodosGraficos()
        })
      } finally {
        document.getElementById('overlayer-loading')?.classList.remove('show-overlayer')
      }
    },
    async carregarFiltros() {
      try {
        const response = await api.get('/painel/ia/relatorios/consumo/filtros', {
          params: this.getParamsBase(),
        })
        this.opcoesFiltro = {
          modelos: uniqueValues(response.data.modelos),
          plataformas: uniqueValues(response.data.plataformas),
          operacoes: uniqueValues(response.data.operacoes),
        }
      } catch (error) {
        console.error('Erro ao carregar filtros de consumo:', error)
      }
    },
    async carregarMetricas() {
      try {
        const response = await api.get('/painel/ia/relatorios/consumo/metricas', {
          params: this.getParamsBase(),
        })
        this.metricas = response.data
      } catch (error) {
        console.error('Erro ao carregar métricas de consumo:', error)
        window.showToastDanger?.('body', 'Erro ao carregar métricas de consumo')
      }
    },
    async carregarSeries() {
      try {
        const params = this.getParamsBase()
        const [tokens, custo, entradaSaida] = await Promise.all([
          api.get('/painel/ia/relatorios/consumo/serie-temporal', {
            params: { ...params, metrica: 'tokens_totais' },
          }),
          api.get('/painel/ia/relatorios/consumo/serie-temporal', {
            params: { ...params, metrica: 'custo_estimado' },
          }),
          api.get('/painel/ia/relatorios/consumo/serie-temporal', {
            params: { ...params, metrica: 'tokens_entrada_saida' },
          }),
        ])

        this.serieTokens = tokens.data
        this.serieCusto = custo.data
        this.serieEntradaSaida = entradaSaida.data
      } catch (error) {
        console.error('Erro ao carregar séries temporais:', error)
        window.showToastDanger?.('body', 'Erro ao carregar gráficos de consumo')
      }
    },
    async carregarInteracoes(pagina = 1) {
      try {
        const response = await api.get('/painel/ia/relatorios/consumo/interacoes', {
          params: {
            ...this.getParamsBase(),
            page: pagina,
            perPage: 20,
          },
        })
        this.interacoes = response.data.interacoes
      } catch (error) {
        console.error('Erro ao carregar interações:', error)
        window.showToastDanger?.('body', 'Erro ao carregar interações')
      }
    },
    aplicarFiltros() {
      this.carregarDados()
    },
    limparFiltros() {
      this.filtros.modelo = ''
      this.filtros.plataforma = ''
      this.filtros.operacao = ''
      this.filtros.oportunidadeId = ''
      this.carregarDados()
    },
    carregarPagina(pagina) {
      if (pagina >= 1 && pagina <= this.interacoes.last_page) {
        this.carregarInteracoes(pagina)
      }
    },
    formatOperacao,
    formatarCusto,
    formatarLatencia,
    abrirModalMetrica(id) {
      this.metricaModal = this.definicoesMetricas[id] || null
      if (!this.metricaModal) {
        return
      }
      this.$nextTick(() => {
        window.$('#modalMetricaConsumoIA').modal('show')
      })
    },
  },
}
